<template>
  <div class="report">
    <p v-if="metaLine" class="report-meta">{{ metaLine }}</p>

    <template v-if="report.invoices.length > 0">
      <p class="detail-title">Factures de clients étrangers · déclarées une à une</p>
      <table class="report-table">
        <thead>
          <tr>
            <th class="col-nowrap">Facture</th>
            <th class="col-nowrap">Date</th>
            <th class="col-truncate">Client</th>
            <th class="col-amount">HT</th>
            <th class="col-amount">TVA</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(invoice, index) in report.invoices" :key="'invoice-' + index">
            <td class="col-nowrap">{{ invoice.number }}</td>
            <td class="col-nowrap">{{ formatDay(invoice.date) }}</td>
            <td class="col-truncate" :title="buyerLabel(invoice)">{{ buyerLabel(invoice) }}</td>
            <td class="col-amount">{{ formatKnownEuro(invoice.taxExclusive) }}</td>
            <td class="col-amount">{{ formatKnownEuro(invoice.taxTotal) }}</td>
          </tr>
        </tbody>
      </table>
    </template>

    <template v-if="report.dailyTransactions.length > 0">
      <p class="detail-title">Ventes à des particuliers · un cumul par jour et par catégorie</p>
      <table class="report-table">
        <thead>
          <tr>
            <th class="col-nowrap">Date</th>
            <th class="col-truncate">Catégorie</th>
            <th class="col-amount">HT</th>
            <th class="col-amount">TVA</th>
            <th class="col-truncate col-rates">Par taux de TVA</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(transactions, index) in report.dailyTransactions" :key="'daily-' + index">
            <td class="col-nowrap">{{ formatDay(transactions.date) }}</td>
            <td class="col-truncate" :title="categoryLabel(transactions.categoryCode)">{{ categoryLabel(transactions.categoryCode) }}</td>
            <td class="col-amount">{{ formatKnownEuro(transactions.taxExclusive) }}</td>
            <td class="col-amount">{{ formatKnownEuro(transactions.taxTotal) }}</td>
            <td class="col-truncate col-rates" :title="vatLinesSummary(transactions.lines)">{{ vatLinesSummary(transactions.lines) }}</td>
          </tr>
        </tbody>
      </table>
    </template>

    <template v-if="report.payments.length > 0">
      <p class="detail-title">Paiements reçus · montants toutes taxes comprises, par taux de TVA</p>
      <table class="report-table">
        <thead>
          <tr>
            <th class="col-truncate">Facture</th>
            <th class="col-nowrap">Date du paiement</th>
            <th class="col-truncate col-rates">Par taux de TVA</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(payment, index) in report.payments" :key="'payment-' + index">
            <td class="col-truncate" :title="paymentInvoiceLabel(payment)">{{ paymentInvoiceLabel(payment) }}</td>
            <td class="col-nowrap">{{ formatDay(payment.date) }}</td>
            <td class="col-truncate col-rates" :title="vatLinesSummary(payment.lines)">{{ vatLinesSummary(payment.lines) }}</td>
          </tr>
        </tbody>
      </table>
    </template>

    <p v-if="isEmpty" class="report-empty">Aucune ligne n'a pu être lue dans ce document : le XML complet est disponible ci-dessous.</p>

    <button type="button" class="xml-toggle" :aria-expanded="showXml" @click="showXml = !showXml">{{ showXml ? 'Masquer le XML' : 'Voir le XML' }}</button>
    <pre v-if="showXml" class="xml">{{ report.xml }}</pre>
  </div>
</template>

<script>
import {categoryLabel, formatDay, formatKnownEuro, formatPeriod, vatLinesSummary} from "@/invoice-transmission/eReportingUtils";

/**
 * Contenu d'une déclaration d'e-reporting, lu dans son XML par ms-invoice-transmission : factures de clients étrangers, cumuls
 * journaliers des ventes aux particuliers et paiements. Le XML d'origine reste consultable.
 */
export default {
  name: 'EReportingReportView',
  props: {
    report: {type: Object, required: true}
  },
  data() {
    return {showXml: false};
  },
  computed: {
    metaLine() {
      const parts = [];
      const period = formatPeriod(this.report.startPeriod, this.report.endPeriod);
      if (period) parts.push(`Période ${period}`);
      if (this.report.issuedAt) parts.push(`document généré le ${formatDay(this.report.issuedAt.substring(0, 10))}`);
      return parts.join(' · ');
    },
    isEmpty() {
      return this.report.invoices.length === 0 && this.report.dailyTransactions.length === 0 && this.report.payments.length === 0;
    }
  },
  methods: {
    formatDay, formatKnownEuro, vatLinesSummary, categoryLabel,
    buyerLabel(invoice) {
      return [invoice.buyerIdentifier, invoice.buyerCountry].filter(Boolean).join(' · ') || '—';
    },
    paymentInvoiceLabel(payment) {
      if (!payment.invoiceNumber) return 'Particuliers (cumul du jour)';
      return payment.invoiceDate ? `${payment.invoiceNumber} du ${formatDay(payment.invoiceDate)}` : payment.invoiceNumber;
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.report-meta
  margin 0 0 10px
  color #4A5F82
  font-size 0.85em
  font-weight 600

.detail-title
  margin 12px 0 6px
  color #1F3A63
  font-size 0.85em
  font-weight 800
  text-transform uppercase

// Jamais plus large que le conteneur : les colonnes de texte sont tronquées (infobulle au survol)
.report-table
  width 100%
  border-collapse collapse
  th, td
    padding 6px 8px
    text-align left
    border-bottom 1px solid #E3ECF8
  th
    color #4A5F82
    font-size 0.8em
    font-weight 800
    white-space nowrap

.col-nowrap
  white-space nowrap

.col-amount
  text-align right !important
  white-space nowrap

.col-truncate
  max-width 0
  width 30%
  min-width 80px
  overflow hidden
  text-overflow ellipsis
  white-space nowrap

.col-rates
  width 38%

.report-empty
  margin 10px 0 0
  color #1F3A63
  font-weight 600

.xml-toggle
  margin-top 12px
  padding 6px 14px
  border 1px solid #D6E3F5
  border-radius 999px
  background white
  color #1F3A63
  font-weight 600
  cursor pointer
  &:hover
    background #EDF3FD

.xml
  box-sizing border-box
  max-width 100%
  max-height 320px
  margin 8px 0 0
  padding 10px 12px
  overflow auto
  border 1px solid #D6E3F5
  border-radius 8px
  background white
  color #17233f
  font-size 0.8em
  line-height 1.4
  white-space pre-wrap
  word-break break-all
</style>
